import { useAtomValue } from 'jotai'
import { motion } from 'motion/react'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import { useAccountsActions } from '@/src/stores/accounts/actions'
import { walletAccountsAtom } from '@/src/stores/accounts/wallets'
import { AnimatedWalletLogo } from '../../connect-wallet/animated-wallet-logo'
import { Button } from '../../ui/button'
import {
  ListRow,
  ListRowSide,
  ListRowSideDescription,
  ListRowSideName,
} from '../../ui/list-row'

export const IgnoredWalletsScreen = () => {
  const { ampli } = useAmplitudeAnalytics()
  const { ignoredGroupedAccounts } = useAtomValue(walletAccountsAtom)
  const { addAccounts, addWalletGroupedAccounts, removeAccounts } =
    useAccountsActions()

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='flex flex-col items-center space-y-6'
    >
      <p className='text-secondary-100 text-sm leading-6'>
        Ignored wallet accounts won’t be displayed while they’re connected to
        Polaris. You may undo this by adding them to your portfolio below.
      </p>

      <section className='py-4 w-full'>
        <div className='flex flex-col items-center gap-2 -mx-4'>
          {ignoredGroupedAccounts.length === 0 && (
            <p className='text-tertiary self-center pb-16 leading-6'>
              No ignored wallets found.
            </p>
          )}
          {ignoredGroupedAccounts.map((account) => (
            <ListRow size='md' className='py-2' key={account.grouping_key}>
              <div className='flex items-center gap-4'>
                {account.provider_info.img && (
                  <AnimatedWalletLogo
                    logoUrl={account.provider_info.img}
                    displayName={account.name}
                    size={40}
                  />
                )}
                <ListRowSide>
                  <ListRowSideName>{account.name}</ListRowSideName>
                  <ListRowSideDescription>
                    {account.provider_info.name}
                  </ListRowSideDescription>
                </ListRowSide>
              </div>
              <Button
                variant='secondary'
                size='sm'
                disabled={addAccounts.isPending}
                onClick={() => {
                  addWalletGroupedAccounts(account)
                }}
              >
                Add
              </Button>
            </ListRow>
          ))}
        </div>
      </section>
      {ignoredGroupedAccounts.length > 0 && (
        <Button
          size='sm'
          variant='outline'
          disabled={removeAccounts.isPending}
          onClick={() => {
            const accounts = ignoredGroupedAccounts.flatMap(
              (account) => account.accounts,
            )

            if (accounts.length > 0) {
              removeAccounts.mutate(accounts)
            }

            const walletSet = new Set(
              ignoredGroupedAccounts.map(
                (account) => account.provider_info.name,
              ),
            )

            if (walletSet.size > 0) {
              for (const wallet of walletSet) {
                ampli?.walletRemoveFromPortfolio({
                  wallet,
                })
              }
            }
          }}
        >
          Reset all
        </Button>
      )}
    </motion.div>
  )
}
